import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Building2, X, Check, ChevronDown, ChevronRight, Users, User } from 'lucide-react';
import { Customer } from '@/types/customer';
import useGlobalCustomerStore, { AccountSelection } from '@/stores/globalCustomerStore';
import { CustomerAccount } from '@/services/socialMediaService';

interface CustomerSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CustomerSelectorModal: React.FC<CustomerSelectorModalProps> = ({ isOpen, onClose }) => {
  const {
    selectedAccount,
    customers,
    customerAccounts,
    loadingCustomers,
    setSelectedAccount,
    loadCustomers
  } = useGlobalCustomerStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());

  // Load customers when modal opens
  useEffect(() => {
    if (isOpen) {
      if (customers.length === 0) loadCustomers();
    }
  }, [isOpen, customers.length, loadCustomers]);

  // Filter customers based on search
  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(customer =>
        customer.customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  // Customer accounts are now loaded with the main customers call, so no need to load them separately

  const handleSelectAllAccounts = (customer: Customer) => {
    const selection: AccountSelection = {
      type: 'all',
      customerId: customer.id,
      customer: customer
    };
    setSelectedAccount(selection);
    onClose();
  };

  const handleSelectIndividualAccount = (customer: Customer, account: CustomerAccount) => {
    const selection: AccountSelection = {
      type: 'individual',
      customerId: customer.id,
      accountId: account.id,
      customer: customer,
      account: account
    };
    setSelectedAccount(selection);
    onClose();
  };

  const toggleCustomerExpansion = (customerId: string) => {
    const newExpanded = new Set(expandedCustomers);
    if (newExpanded.has(customerId)) {
      newExpanded.delete(customerId);
    } else {
      newExpanded.add(customerId);
      // Accounts are already loaded with the customers call
    }
    setExpandedCustomers(newExpanded);
  };

  const isSelected = (customer: Customer, account?: CustomerAccount) => {
    if (!selectedAccount || selectedAccount.customerId !== customer.id) {
      return false;
    }
    
    if (account) {
      return selectedAccount.type === 'individual' && selectedAccount.accountId === account.id;
    } else {
      return selectedAccount.type === 'all';
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed left-4 top-20 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[calc(100vh-6rem)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search for a sub-account..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-md transition-colors"
                    title="Clear search"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Customer List */}
            <div className="overflow-y-auto max-h-96">
              <div className="px-4 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  ALL ACCOUNTS
                </h3>
              </div>

              {loadingCustomers ? (
                <div className="px-4 py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading customers...</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-gray-500">
                    {searchTerm ? 'No customers found' : 'No active customers'}
                  </p>
                </div>
              ) : (
                <div className="pb-2">
                  {filteredCustomers.map((customer) => (
                    <div key={customer.id} className="border-b border-gray-100 last:border-b-0">
                      {/* Customer Header */}
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleCustomerExpansion(customer.id)}
                          className="flex-1 px-4 py-3 hover:bg-gray-50 transition-colors text-left group flex items-center space-x-3"
                        >
                          <div className="w-10 h-10 bg-gray-200 group-hover:bg-gray-300 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {customer.customerName}
                              </p>
                              {expandedCustomers.has(customer.id) ? (
                                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {customerAccounts[customer.id]?.length ?? customer.totalPlatformAccounts} accounts • {customer.totalPublications} publications
                            </p>
                          </div>
                        </button>
                      </div>

                      {/* Expanded Content */}
                      <AnimatePresence>
                        {expandedCustomers.has(customer.id) && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden bg-gray-50"
                          >
                            {/* All Accounts Option */}
                            <button
                              onClick={() => handleSelectAllAccounts(customer)}
                              className={`w-full px-8 py-2 hover:bg-gray-100 transition-colors text-left group ${
                                isSelected(customer) ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                  isSelected(customer)
                                    ? 'bg-blue-500' 
                                    : 'bg-gray-300 group-hover:bg-gray-400'
                                }`}>
                                  <Users className={`h-4 w-4 ${
                                    isSelected(customer) ? 'text-white' : 'text-gray-600'
                                  }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className={`text-sm font-medium truncate ${
                                      isSelected(customer) ? 'text-blue-600' : 'text-gray-900'
                                    }`}>
                                      All Accounts
                                    </p>
                                    {isSelected(customer) && (
                                      <Check className="h-4 w-4 text-blue-500 flex-shrink-0 ml-2" />
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    View data for all {customer.totalPlatformAccounts} accounts
                                  </p>
                                </div>
                              </div>
                            </button>

                            {/* Individual Accounts */}
                            {customerAccounts[customer.id] ? (
                              customerAccounts[customer.id].map((account) => (
                                <button
                                  key={account.id}
                                  onClick={() => handleSelectIndividualAccount(customer, account)}
                                  className={`w-full px-8 py-2 hover:bg-gray-100 transition-colors text-left group ${
                                    isSelected(customer, account) ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      isSelected(customer, account)
                                        ? 'bg-blue-500' 
                                        : 'bg-gray-300 group-hover:bg-gray-400'
                                    }`}>
                                      <User className={`h-4 w-4 ${
                                        isSelected(customer, account) ? 'text-white' : 'text-gray-600'
                                      }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <p className={`text-sm font-medium truncate ${
                                          isSelected(customer, account) ? 'text-blue-600' : 'text-gray-900'
                                        }`}>
                                          {account.displayName}
                                        </p>
                                        {isSelected(customer, account) && (
                                          <Check className="h-4 w-4 text-blue-500 flex-shrink-0 ml-2" />
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        @{account.username} • {account.platformDisplayName}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))
                            ) : null}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CustomerSelectorModal;